import { useCallback, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { Sprout } from "lucide-react";
import { Input } from "@/components/ui/input";
import { buscarCultivos, type CultivoCatalogoItem } from "@/lib/crops/catalogo";
import { estaDeBaja } from "@/lib/crops/resolver";
import { cn } from "@/lib/utils";
import { ListaEnLinea, opcionId, teclaLista, useCerrarAlTocarFuera, type OpcionLista } from "./ListaEnLinea";

/**
 * EL SELECTOR DE CULTIVO DEL CATÁLOGO SIEX (plan cultivos SIEX F3, 01-10-2026).
 *
 * «tom» → Tomate · Solanum lycopersicum, debajo del campo y sin esperar a nadie:
 * el catálogo ya está en memoria (lo trae `CropVarietyFields` una vez) y buscar
 * es `buscarCultivos`, síncrono. Ninguna tecla pide nada a la red (guard
 * `cropPickerSinRed`).
 *
 * El orden lo pone `buscarCultivos` (tipo de coincidencia en el idioma de la
 * pantalla > los que ya usa la empresa > los de invernadero > hortícolas); los
 * dados de baja en SIEX no salen salvo que sean el valor actual, y entonces
 * llevan su etiqueta «baja SIEX». Elegido, el cultivo queda como una pastilla
 * con «Cambiar»: lo elegido se ve de un vistazo y no se reescribe sin querer.
 *
 * CULTIVO PROPIO (09-10-2026, orden de Pablo: «que se pueda escribir
 * CUALQUIER cultivo»). Con 2 letras o más escritas y el catálogo cargado, la
 * ÚLTIMA fila fija es «Usar «X» como cultivo propio» —también cuando hay
 * resultados parecidos—, salvo que lo escrito sea TAL CUAL un cultivo del
 * catálogo (coincidencia «exacta»: nombre oficial, castellano, alias o el
 * nombre en el idioma de la pantalla): entonces se elige ese, con su código.
 * Elegido, la pastilla lo dice («Cultivo propio · sin código SIEX») y se guarda
 * sin código (`onPickOwn`, ver `types.ts#cropOwn`).
 *
 * Campo de 16 px en el teléfono (con menos, iOS hace zoom al enfocarlo) y filas
 * de 44 px.
 */

/** Las filas que caben sin que la lista empuje el formulario fuera de la pantalla. */
const MAX_FILAS = 8;
/** Letras escritas a partir de las que se ofrece «Usar «X» como cultivo propio». */
export const MIN_LETRAS_PROPIO = 2;

/** Lo escrito como cultivo propio: sin espacios de más (lo mismo que hace el trigger con `crop_name`). */
export const textoPropio = (q: string): string => q.replace(/\s+/g, " ").trim();

export interface CropPickerProps {
  /** El catálogo ya cargado (`crop_catalog_list`). Vacío mientras llega. */
  items: readonly CultivoCatalogoItem[];
  /** El catálogo viaja: hueco en la lista, pero se puede ir escribiendo. */
  loading?: boolean;
  /** El cultivo puesto: código (null si no hay o es texto viejo), nombre y si es un cultivo propio elegido a propósito. */
  value: { code: number | null; name: string; own?: boolean };
  onPick: (item: CultivoCatalogoItem) => void;
  /** «Usar «X» como cultivo propio» (sin código). Sin la prop, la opción no sale. */
  onPickOwn?: (name: string) => void;
  /** Códigos que ya usa la empresa: a igual coincidencia, primero. */
  usados?: readonly number[];
  invalid?: boolean;
  /** id del campo (para su `<Label htmlFor>`). */
  id: string;
  compact?: boolean;
  disabled?: boolean;
}

/** El nombre del cultivo en el idioma de la pantalla o, si no se sabe, el castellano (igual que `buscarCultivos`). */
export function etiquetaCultivo(item: CultivoCatalogoItem, lang: string): string {
  const l = lang.split(/[-_]/)[0].toLowerCase();
  return (l !== "es" && item.i?.[l as keyof NonNullable<CultivoCatalogoItem["i"]>]) || item.n;
}

const hoyIso = () => new Date().toISOString().slice(0, 10);

export function CropPicker({ items, loading = false, value, onPick, onPickOwn, usados, invalid = false, id, compact = false, disabled = false }: CropPickerProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language ?? "es";
  const [q, setQ] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [activa, setActiva] = useState(0);
  const [cambiando, setCambiando] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const listaId = `${id}-lista`;

  const tieneCodigo = value.code != null;
  const esPropio = !tieneCodigo && value.own === true && value.name.trim().length > 0;
  const tieneValor = tieneCodigo || esPropio;
  const verPastilla = tieneValor && !cambiando;
  const actual = useMemo(() => (tieneCodigo ? items.find((c) => c.c === value.code) ?? null : null), [items, tieneCodigo, value.code]);

  /* Una búsqueda por pulsación, en memoria y solo con la lista abierta. */
  const resultados = useMemo(
    () => (abierta && !verPastilla ? buscarCultivos(items, q, { lang, usados, actual: value.code, limit: MAX_FILAS }) : []),
    [abierta, verPastilla, items, q, lang, usados, value.code],
  );

  const cerrar = useCallback(() => {
    setAbierta(false);
    /* Cambiando un cultivo ya puesto sin haber escrito nada: vuelve la pastilla. */
    if (tieneValor && !q.trim()) setCambiando(false);
  }, [tieneValor, q]);
  useCerrarAlTocarFuera(caja, abierta, cerrar);

  const elegir = (item: CultivoCatalogoItem) => {
    onPick(item);
    setQ("");
    setAbierta(false);
    setCambiando(false);
    setActiva(0);
  };

  const elegirPropio = (texto: string) => {
    onPickOwn?.(texto);
    setQ("");
    setAbierta(false);
    setCambiando(false);
    setActiva(0);
  };

  const escrito = textoPropio(q);
  /* La exacta, si la hay, va la primera (`buscarCultivos` la pone delante y nunca la filtra). */
  const hayExacta = resultados.some((r) => r.coincidencia === "exacta");
  const ofrecePropio =
    !!onPickOwn && abierta && !verPastilla && !loading && items.length > 0 && escrito.length >= MIN_LETRAS_PROPIO && !hayExacta;

  const opciones: OpcionLista[] = resultados.map((r) => ({
    key: String(r.item.c),
    testId: `crop-option-${r.item.c}`,
    onElegir: () => elegir(r.item),
    contenido: (
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate">
          <span className="font-semibold text-[#0c1f3d]">{r.etiqueta}</span>
          {r.item.l && <span className="text-muted-foreground"> · <i>{r.item.l}</i></span>}
        </span>
        {r.deBaja && <BajaSiex />}
      </span>
    ),
  }));
  if (ofrecePropio) {
    opciones.push({
      key: "propio",
      fija: true,
      testId: "crop-use-own",
      onElegir: () => elegirPropio(escrito),
      /* Sin recortar: a 375 px «… como cultivo propio» es justo lo que se perdía (e2e 09-10-2026). */
      contenido: <span className="min-w-0 whitespace-normal break-words">{t("crops.own.use", { name: escrito, defaultValue: "Usar «{{name}}» como cultivo propio" })}</span>,
    });
  }

  const sinResultados = abierta && !loading && q.trim().length > 0 && resultados.length === 0;
  /* Escrito pero sin elegir: lo escrito no es un cultivo hasta que se elige de la lista (o se usa como propio). */
  const pideElegir = (invalid && !tieneValor) || (!abierta && q.trim().length > 0);
  const avisoSinResultados = sinResultados ? (
    <p className="px-3 py-2.5 text-sm text-muted-foreground" role="status">
      {t("crops.search.noResults", { q: q.trim() })}
    </p>
  ) : null;

  if (verPastilla) {
    const etiqueta = actual ? etiquetaCultivo(actual, lang) : value.name;
    const deBaja = actual ? estaDeBaja(actual.b, hoyIso()) : false;
    return (
      <div ref={caja} className="mt-1">
        <div
          className={cn(
            "flex min-h-[44px] items-center gap-2 rounded-xl border border-[#b6dded] bg-[#f4f9ff] py-1 pl-3 pr-1",
            compact && "min-h-[40px]",
          )}
          data-testid="crop-chip"
        >
          <Sprout className="h-4 w-4 shrink-0 text-[#16A971]" aria-hidden />
          {esPropio ? (
            /* Las dos líneas se parten en vez de cortarse: en la columna estrecha del ordenador
               «sin código SIEX» quedaba fuera, y es lo que hay que ver (e2e 09-10-2026). */
            <span className="min-w-0 flex-1 py-0.5 text-[15px] md:text-sm" data-own="true">
              <span className="block break-words font-semibold leading-snug text-[#0c1f3d]">{etiqueta}</span>
              <span className="block break-words text-xs font-medium leading-snug text-[#b5701b]" data-testid="crop-own-badge">
                {t("crops.own.badge", { defaultValue: "Cultivo propio · sin código SIEX" })}
              </span>
            </span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-[15px] md:text-sm">
              <span className="font-semibold text-[#0c1f3d]">{etiqueta}</span>
              {actual?.l && <span className="text-muted-foreground"> · <i>{actual.l}</i></span>}
            </span>
          )}
          {deBaja && <BajaSiex />}
          {!disabled && (
            <button
              type="button"
              id={id}
              data-testid="crop-change"
              onClick={() => {
                /* flushSync + focus en el MISMO toque: el campo se monta ya en
                   este clic (y `campo` apunta a él); si el foco llegara en otro
                   tick, iOS no abriría el teclado. */
                flushSync(() => {
                  setCambiando(true);
                  setAbierta(true);
                  setActiva(0);
                });
                campo.current?.focus();
              }}
              className="min-h-[40px] shrink-0 rounded-lg px-3 text-sm font-semibold text-[#1d5fa8] hover:bg-[#e3f0fc] active:bg-[#e3f0fc]"
            >
              {t("crops.change")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={caja}>
      <Input
        ref={campo}
        id={id}
        value={q}
        disabled={disabled}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={abierta}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-invalid={pideElegir || undefined}
        aria-activedescendant={abierta && opciones.length > 0 && activa >= 0 ? opcionId(listaId, activa) : undefined}
        placeholder={t("crops.search.placeholder")}
        data-testid="crop-search"
        className={cn(compact && "h-10", pideElegir && "border-[#E5574E] focus-visible:border-[#E5574E]")}
        onFocus={() => setAbierta(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setAbierta(true);
          setActiva(0);
        }}
        onBlur={(e) => {
          /* Solo el tabulador (el foco se va a otro campo de fuera). Un toque fuera
             lo cierra `useCerrarAlTocarFuera`; un toque en una fila no debe cerrar. */
          const a = e.relatedTarget as Node | null;
          if (a && caja.current && !caja.current.contains(a)) cerrar();
        }}
        onKeyDown={(e) => {
          if (teclaLista(e, { abierta, opciones, activa, setActiva, cerrar })) return;
          if (e.key === "Escape" && cambiando) {
            setCambiando(false);
            setQ("");
          }
        }}
      />
      {abierta && (opciones.length > 0 || loading || sinResultados) && (
        <ListaEnLinea
          id={listaId}
          aria-label={t("crops.label")}
          opciones={opciones}
          activa={activa}
          onActiva={setActiva}
          cargando={loading && items.length === 0}
          /* Sin resultados y con «Usar «X» como cultivo propio» debajo, el aviso va ENCIMA: primero
             «no está en el catálogo» y luego qué hacer. */
          cabecera={ofrecePropio ? avisoSinResultados : null}
          pie={ofrecePropio ? null : avisoSinResultados}
        />
      )}
      {pideElegir && (
        <p className="mt-1 text-xs font-medium text-[#E5574E]" role="alert">
          {onPickOwn
            ? t("crops.own.pickRequired", { defaultValue: "Elige un cultivo de la lista o usa lo escrito como cultivo propio" })
            : t("crops.pickRequired")}
        </p>
      )}
    </div>
  );
}

function BajaSiex() {
  const { t } = useTranslation();
  return (
    <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground" data-testid="crop-withdrawn">
      {t("crops.withdrawn")}
    </span>
  );
}

export default CropPicker;
